import { STEMS, type BufferPort, type BufferedTransportPort, type ChannelState, type GainPort, type GraphPort, type IMixerEngine, type MixerSnapshot, type NodePort, type PlaybackSettings, type Section, type SourcePort, type StemId, type StemInputs } from './types';
import { inspectWav } from './wav';
import { audioFormat, decodedByteSize } from './formats';
import { sha256 } from './hash';
import { readAudioMetadata } from './metadata';
import { buildLoopBank, validateSection } from './loop-bank';
export const TRANSPORT_RAMP_SECONDS = .020;
export const START_LEAD_SECONDS = .100;
const MAX_DECODED_BYTES = 512 * 1024 * 1024;
export const projectedTooLongMessage = (duration: number, sampleRate: number) => {
  const maximumSeconds = MAX_DECODED_BYTES / (STEMS.length * sampleRate * 8);
  const wholeSeconds = Math.max(0, Math.floor(maximumSeconds));
  return `This song is too long for this device (maximum about ${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, '0')})`;
};
const message = (e: unknown) => e instanceof Error ? e.message : String(e);
const emptyChannels = () => Object.fromEntries(STEMS.map(id => [id, { gain: 1, muted: false, solo: false, status: 'empty', peak: 0 }])) as Record<StemId, ChannelState>;

export class MixerEngine implements IMixerEngine {
  protected graph: GraphPort;
  private master: GainPort;
  protected faders: Record<StemId, GainPort>;
  private buffers: Partial<Record<StemId, BufferPort>> = {};
  private loadedInputs: StemInputs = {};
  private banks: Partial<Record<StemId, ReturnType<typeof buildLoopBank>>> = {};
  private voices: SourcePort[] = [];
  private listeners = new Set<(state: MixerSnapshot) => void>();
  private interval: ReturnType<typeof setInterval>;
  private status: MixerSnapshot['status'] = 'empty';
  private channels = emptyChannels();
  private duration = 0;
  private sampleRate = 0;
  private position = 0;
  private loop: Section | null = null;
  private clockStart = 0;
  private offset = 0;
  private disposed = false;
  private operation = 0;
  private error?: string;
  private playback: PlaybackSettings = { semitones: 0, rate: 1 };
  private diagnostics: MixerSnapshot['diagnostics'] = { startTime: 0, startSpreadFrames: 0, loopPasses: 0, loopPeriodFrames: 0, decodedBytes: 0, encodedBytes: 0, bankBytes: 0, ceilingDb: -1, scheduler: 'Shared audio clock · 100ms start lead', interruptions: [] };
  constructor(graph: GraphPort, limiter: NodePort, private transport?: BufferedTransportPort) {
    this.graph = graph;
    this.master = graph.createGain(); this.master.gain.value = 0;
    this.master.connect(limiter); limiter.connect(graph.destination);
    this.faders = Object.fromEntries(STEMS.map(id => { const fader = graph.createGain(); fader.connect(this.master); return [id, fader]; })) as Record<StemId, GainPort>;
    this.interval = setInterval(() => {
      if (this.status === 'playing' && !this.loop && this.elapsedPosition() >= this.duration) { this.position = this.duration; this.status = 'ready'; this.stopVoices(this.graph.currentTime); }
      this.emit();
    }, 100);
  }
  async load(inputs: StemInputs): Promise<void> {
    this.assertLive();
    if (this.status === 'loading') throw new Error('A load is already in progress.');
    this.pause(); const operation = ++this.operation; this.stopVoices(this.graph.currentTime);
    // Release the previous song before allocating the next song's decoded PCM.
    this.status = 'loading'; this.error = undefined; this.loop = null; this.position = 0; this.duration = 0;
    if (this.transport) try { await this.transport.load({}); } catch(e) { this.reportError(message(e)); throw e; }
    const previousBuffers = this.buffers, previousInputs = this.loadedInputs, previousChannels = this.channels;
    this.buffers = {}; this.loadedInputs = {}; this.banks = {}; this.channels = emptyChannels();
    this.diagnostics.decodedBytes = 0; this.diagnostics.bankBytes = 0; this.diagnostics.encodedBytes = 0;
    const preflightBytes = new Map<StemId, ArrayBuffer>();
    const firstInput = inputs[STEMS[0]];
    if (firstInput) {
      const previousFirst = previousBuffers[STEMS[0]], previousFirstState = previousChannels[STEMS[0]];
      const reusableFirst = !this.transport && firstInput && previousFirst && previousFirstState.status === 'ready' && (firstInput === previousInputs[STEMS[0]] || firstInput.expectedHash && firstInput.expectedHash.toLowerCase() === previousFirstState.hash);
      let firstDuration = reusableFirst ? previousFirst!.duration : undefined;
      if (firstDuration === undefined) {
        const firstBytes = firstInput.bytes ?? await firstInput.read?.();
        if (!firstBytes) throw new Error('No local audio reader supplied.');
        const firstMetadata = await readAudioMetadata(firstBytes, firstInput.name);
        firstDuration = firstMetadata.duration;
        preflightBytes.set(STEMS[0], firstBytes);
      }
      if (firstDuration && STEMS.length * firstDuration * this.graph.sampleRate * 8 > MAX_DECODED_BYTES) {
        const error = projectedTooLongMessage(firstDuration, this.graph.sampleRate);
        this.status = 'error'; this.error = error; this.emit(); throw new Error(error);
      }
    }
    for (const id of STEMS) {
      const input = inputs[id], previous = previousBuffers[id];
      const reusable = !this.transport && input && previous && previousChannels[id].status === 'ready' && (input === previousInputs[id] || input.expectedHash && input.expectedHash.toLowerCase() === previousChannels[id].hash);
      this.channels[id] = { ...previousChannels[id], status: input ? 'loading' : 'unavailable', filename: input?.name, hash: undefined, metadata: undefined, error: input ? undefined : 'No file selected' };
      if (reusable) { this.buffers[id] = previous; this.loadedInputs[id] = input; this.channels[id] = { ...previousChannels[id], filename: input.name }; this.diagnostics.decodedBytes += decodedByteSize(previous); }
      delete previousBuffers[id];
    }
    this.emit();
    const checkOperation = () => { if (this.disposed || operation !== this.operation) throw new Error('Audio loading canceled.'); };
    for (const id of STEMS) {
      checkOperation();
      const input = inputs[id]; if (!input) continue;
      this.diagnostics.encodedBytes += input.encodedSize ?? input.bytes?.byteLength ?? 0;
      if (this.buffers[id]) continue;
      try {
        const bytes = preflightBytes.get(id) ?? input.bytes ?? await input.read?.();
        checkOperation();
        if (!bytes) throw new Error('No local audio reader supplied.');
        if (bytes.byteLength > 400 * 1024 * 1024) throw new Error('Stem exceeds 400 MiB.');
        const format = audioFormat(input.name, bytes), hash = sha256(bytes);
        if (input.expectedHash && hash !== input.expectedHash.toLowerCase()) throw new Error('SHA-256 mismatch. Re-download this stem.');
        this.channels[id].hash = hash;
        const metadata = await readAudioMetadata(bytes, input.name);
        checkOperation(); this.channels[id].metadata = metadata;
        if (metadata.duration && STEMS.length * metadata.duration * this.graph.sampleRate * 8 > MAX_DECODED_BYTES) throw new Error(projectedTooLongMessage(metadata.duration, this.graph.sampleRate));
        if (!input.encodedSize && !input.bytes) this.diagnostics.encodedBytes += bytes.byteLength;
        if (format === 'wav') {
          const info = inspectWav(bytes), estimate = Math.ceil(info.duration * this.graph.sampleRate) * 2 * 4;
          if (this.diagnostics.decodedBytes + estimate > MAX_DECODED_BYTES) throw new Error('Decoded audio exceeds the 512 MiB budget.');
        }
        // Decode sequentially into the final graph buffer. No PCM staging arrays
        // or duplicate AudioBuffer copies; encoded byte arrays can be collected.
        const buffer = await this.graph.decodeAudioData(bytes);
        checkOperation();
        if (buffer.numberOfChannels !== 2) throw new Error('Select a stereo stem.');
        if (!Number.isFinite(buffer.duration) || buffer.duration <= 0 || !Number.isInteger(buffer.length) || buffer.length <= 0 || buffer.sampleRate !== this.graph.sampleRate) throw new Error('Invalid decoded duration or sample rate.');
        const memory = decodedByteSize(buffer);
        if (this.diagnostics.decodedBytes + memory > MAX_DECODED_BYTES) throw new Error('Decoded audio exceeds the 512 MiB budget.');
        let peak=0;
        for (let channel = 0; channel < 2; channel++) for (const sample of buffer.getChannelData(channel)) { if (!Number.isFinite(sample)) throw new Error('Audio contains a non-finite sample.'); peak=Math.max(peak,Math.abs(sample)); }
        this.channels[id].peak=peak;
        this.buffers[id] = buffer; this.loadedInputs[id] = input;
        this.channels[id].status = 'ready'; this.diagnostics.decodedBytes += memory;
      } catch (e) { this.channels[id].status = 'unavailable'; this.channels[id].error = message(e); }
      this.emit(); await new Promise(resolve => setTimeout(resolve, 0));
    }
    checkOperation();
    const counts = new Map<string, number>();
    for (const buffer of Object.values(this.buffers)) { const key = `${buffer.sampleRate}:${buffer.length}`; counts.set(key, (counts.get(key) ?? 0) + 1); }
    const canonical = [...counts.entries()].sort((a,b) => b[1] - a[1])[0]?.[0];
    for (const id of STEMS) {
      const buffer = this.buffers[id]; if (!buffer) continue;
      if (`${buffer.sampleRate}:${buffer.length}` !== canonical) {
        this.channels[id].status = 'unavailable';
        this.channels[id].error = 'Decoded stem length differs. Export all four over the same range using consistent encoder settings.';
        this.diagnostics.decodedBytes -= decodedByteSize(buffer); delete this.buffers[id]; delete this.loadedInputs[id];
      } else { this.duration = buffer.duration; this.sampleRate = buffer.sampleRate; }
    }
    // Gapless metadata describes encoder priming. Never trim it a second time:
    // platform decoders may already apply it. Reject inconsistent export headers.
    const signatures = new Map<string, number>();
    const signature = (id: StemId) => {
      const m = this.channels[id].metadata;
      return m?.originalFrames && m.sampleRate ? `${m.sampleRate}:${m.encoderDelay}:${m.encoderPadding}:${m.originalFrames}` : undefined;
    };
    for (const id of STEMS) if (this.buffers[id]) { const s = signature(id); if(s) signatures.set(s,(signatures.get(s)??0)+1); }
    const expected = [...signatures].sort((a,b)=>b[1]-a[1])[0]?.[0];
    if (signatures.size > 1) for (const id of STEMS) {
      const s = signature(id), buffer = this.buffers[id];
      if (buffer && s && s !== expected) {
        this.channels[id].status='unavailable'; this.channels[id].error='Encoder timing differs between stems. Export them together over the same range.';
        this.diagnostics.decodedBytes -= decodedByteSize(buffer); delete this.buffers[id]; delete this.loadedInputs[id];
      }
    }
    const ready = Object.keys(this.buffers).length;
    if (this.transport) {
      try { await this.transport.load(ready === 4 ? this.buffers : {}); checkOperation(); }
      catch (e) { this.status = 'error'; this.error = `Audio processor: ${message(e)}`; this.emit(); throw e; }
    }
    this.status = ready === 4 ? 'ready' : ready ? 'partial' : 'error';
    if (ready !== 4) this.error = ready ? `${ready}/4 stems decoded. Load four valid, aligned stereo stems to play.` : 'No valid audio stems. Check the channel messages.';
    this.updateGains(); this.emit();
  }
  async play(): Promise<void> {
    this.assertLive();
    if (this.status === 'playing') return;
    if (!['ready', 'paused'].includes(this.status) || Object.keys(this.buffers).length !== 4) throw new Error('Finish loading and validating all four stems before playing.');
    const operation = ++this.operation;
    await this.graph.resume();
    if (this.disposed || operation !== this.operation) return;
    if (this.position >= this.duration) this.position = 0;
    if (this.loop && !this.transport) this.prepareBanks();
    this.stopVoices(this.graph.currentTime);
    const when = this.graph.currentTime + Math.max(START_LEAD_SECONDS, this.transport?.leadSeconds ?? 0);
    this.clockStart = when; this.offset = this.position;
    const starts: number[] = [];
    if (this.transport) {
      let startWhen = when;
      try {
        await this.transport.start(when, this.position, this.playback, this.loop);
      } catch (error) {
        if (!message(error).includes('Audio scheduling took too long')) throw error;
        const retryLead = Math.min(1, Math.max(START_LEAD_SECONDS, this.transport.leadSeconds ?? 0) + .250);
        const retryWhen = this.graph.currentTime + retryLead;
        startWhen = retryWhen;
        this.clockStart = retryWhen;
        this.transport.leadSeconds = retryLead;
        await this.transport.start(retryWhen, this.position, this.playback, this.loop);
        this.diagnostics.scheduler = `Stem-aware pitch/tempo processing · shared audio-clock timeline · retry lead ${Math.round(retryLead * 1000)}ms`;
      }
      if (this.disposed || operation !== this.operation) return;
      starts.push(startWhen);
    } else for (const id of STEMS) {
      const buffer = this.buffers[id]; if (!buffer) continue;
      if (this.loop) {
        const bank = this.banks[id]!;
        const period = bank.periodFrames / this.sampleRate;
        const phase = Math.max(0, Math.min(period - 1 / this.sampleRate, this.position - this.loop.start));
        // Both recurring banks are installed ahead of the first boundary. Scheduling
        // does not depend on worker callbacks or JS wakeups after this point.
        for (const offset of [phase, phase + period]) {
          const source = this.graph.createBufferSource(); source.buffer = bank.buffer; source.loop = true;
          source.loopStart = 0; source.loopEnd = period * 2; source.connect(this.faders[id]);
          source.start(when, offset); this.voices.push(source); starts.push(when);
        }
      } else {
        const source = this.graph.createBufferSource(); source.buffer = buffer; source.connect(this.faders[id]);
        source.start(when, this.position); source.stop(when + this.duration - this.position);
        this.voices.push(source); starts.push(when);
      }
    }
    this.master.gain.cancelScheduledValues(this.graph.currentTime);
    this.master.gain.setValueAtTime(0, this.graph.currentTime);
    // clockStart equals `when`, except after a slow-start retry moved the real start later.
    this.master.gain.setValueAtTime(0, this.clockStart);
    this.master.gain.linearRampToValueAtTime(1, this.clockStart + TRANSPORT_RAMP_SECONDS);
    this.diagnostics.startTime = this.clockStart;
    this.diagnostics.startSpreadFrames = (Math.max(...starts) - Math.min(...starts)) * this.graph.sampleRate;
    this.status = 'playing'; this.emit();
  }
  pause(reason?: string): void {
    if (this.status === 'loading') return;
    this.operation++;
    if (this.status !== 'playing') { this.transport?.stop(this.graph.currentTime); return; }
    this.position = this.elapsedPosition();
    const now = this.graph.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(0, now + TRANSPORT_RAMP_SECONDS);
    this.stopVoices(now + TRANSPORT_RAMP_SECONDS);
    this.status = 'paused';
    if (reason) this.diagnostics.interruptions = [...this.diagnostics.interruptions.slice(-7), reason];
    this.emit();
  }
  async seek(seconds: number): Promise<void> {
    this.assertLive();
    if (this.status === 'loading') throw new Error('Finish loading before seeking.');
    if (!Number.isFinite(seconds)) throw new Error('Seek position must be finite.');
    const playing = this.status === 'playing'; this.pause();
    const operation = ++this.operation;
    const start = this.loop?.start ?? 0, end = this.loop?.end ?? this.duration;
    this.position = Math.round(Math.max(start, Math.min(end - (this.loop ? 1 / this.sampleRate : 0), seconds)) * (this.sampleRate || 1)) / (this.sampleRate || 1);
    this.emit();
    if (playing && this.position < this.duration) { await new Promise(resolve => setTimeout(resolve, 25)); if (operation === this.operation && !this.disposed) await this.play(); }
  }
  async skip(seconds: number): Promise<void> { await this.seek(this.elapsedPosition() + seconds * this.playback.rate); }
  async setPlaybackSettings(settings: PlaybackSettings): Promise<void> {
    this.assertLive();
    if (!this.transport) throw new Error('Independent key and tempo controls are currently available in the web mixer.');
    if (this.status === 'loading') throw new Error('Finish loading before changing key or tempo.');
    if (!Number.isInteger(settings.semitones) || settings.semitones < -12 || settings.semitones > 12) throw new Error('Choose a shift between −12 and +12 semitones.');
    if (!Number.isFinite(settings.rate) || settings.rate < .5 || settings.rate > 1.5) throw new Error('Choose a tempo between 50% and 150% of the original BPM.');
    if (settings.semitones === this.playback.semitones && settings.rate === this.playback.rate) return;
    const playing = this.status === 'playing'; this.pause(); const operation = ++this.operation;
    this.playback = { ...settings }; this.emit();
    if (playing) { await new Promise(resolve => setTimeout(resolve, 25)); if (operation === this.operation && !this.disposed) await this.play(); }
  }
  setGain(id: StemId, gain: number): void { if (!Number.isFinite(gain)) return; this.channels[id].gain = Math.max(0, Math.min(1.5, gain)); this.updateGains(); this.emit(); }
  setMute(id: StemId, muted: boolean): void { this.channels[id].muted = muted; this.updateGains(); this.emit(); }
  setSolo(id: StemId, solo: boolean): void { this.channels[id].solo = solo; this.updateGains(); this.emit(); }
  async setLoop(section: Section | null): Promise<void> {
    if (!this.duration || this.status === 'loading') throw new Error('Load stems first.');
    if (section) validateSection(section, this.duration);
    // Build before interrupting current playback; reject without changing transport.
    if (section && !this.transport) {
      const total = Math.round((section.end - section.start) * this.sampleRate) * 2 * 2 * 4 * Object.keys(this.buffers).length;
      if (this.diagnostics.decodedBytes + total > MAX_DECODED_BYTES) throw new Error('Loop banks would exceed the 512 MiB audio budget. Select a shorter section.');
    }
    const playing = this.status === 'playing'; this.pause(); const operation = ++this.operation;
    this.loop = section ? { ...section, start: Math.round(section.start * this.sampleRate) / this.sampleRate, end: Math.round(section.end * this.sampleRate) / this.sampleRate } : null;
    this.banks = {}; this.diagnostics.bankBytes = 0; this.diagnostics.loopPeriodFrames = 0;
    if (this.loop) { this.position = this.loop.start; if (!this.transport) this.prepareBanks(); }
    this.emit();
    if (playing) { await new Promise(resolve => setTimeout(resolve, 25)); if (operation === this.operation && !this.disposed) await this.play(); }
  }
  snapshot(): MixerSnapshot {
    const position = this.elapsedPosition();
    const elapsed = Math.max(0, this.graph.currentTime - this.clockStart) * this.playback.rate;
    const loopPasses = this.loop && this.status === 'playing' ? Math.floor((elapsed + this.offset - this.loop.start) / (this.loop.end - this.loop.start)) : 0;
    return { status: this.status, position, duration: this.duration, sampleRate: this.sampleRate, loop: this.loop ? { ...this.loop } : null, channels: Object.fromEntries(STEMS.map(id => [id, { ...this.channels[id], metadata: this.channels[id].metadata ? {...this.channels[id].metadata} : undefined }])) as Record<StemId, ChannelState>, diagnostics: { ...this.diagnostics, loopPasses, scheduler: this.transport ? 'Stem-aware pitch/tempo processing · shared audio-clock timeline' : this.diagnostics.scheduler, interruptions: [...this.diagnostics.interruptions] }, playback: { ...this.playback }, pitchTempoSupported: !!this.transport, error: this.error };
  }
  subscribe(listener: (state: MixerSnapshot) => void): () => void { this.listeners.add(listener); listener(this.snapshot()); return () => this.listeners.delete(listener); }
  async dispose(): Promise<void> { this.pause(); this.disposed = true; clearInterval(this.interval); this.stopVoices(this.graph.currentTime); this.transport?.dispose(); this.listeners.clear(); this.buffers = {}; this.loadedInputs = {}; this.banks = {}; await this.graph.close(); }
  private prepareBanks(): void {
    if (!this.loop || Object.keys(this.banks).length) return;
    for (const id of STEMS) {
      const buffer = this.buffers[id]; if (!buffer) continue;
      const bank = buildLoopBank(this.graph, buffer, this.loop); this.banks[id] = bank;
      this.diagnostics.bankBytes += bank.buffer.length * 2 * 4;
      this.diagnostics.loopPeriodFrames = bank.periodFrames;
    }
  }
  private elapsedPosition(): number {
    if (this.status !== 'playing') return this.position;
    const elapsed = Math.max(0, this.graph.currentTime - this.clockStart) * this.playback.rate;
    if (this.loop) { const p = this.loop.end - this.loop.start; return this.loop.start + (elapsed + this.offset - this.loop.start) % p; }
    return Math.min(this.duration, this.offset + elapsed);
  }
  private updateGains(): void {
    const anySolo = STEMS.some(id => this.channels[id].status === 'ready' && this.channels[id].solo);
    const now = this.graph.currentTime;
    for (const id of STEMS) {
      const channel = this.channels[id], audible = channel.status === 'ready' && !channel.muted && (!anySolo || channel.solo);
      const param = this.faders[id].gain;
      param.cancelScheduledValues(now); param.setValueAtTime(param.value, now);
      param.linearRampToValueAtTime(audible ? channel.gain : 0, now + .005);
    }
  }
  private stopVoices(when: number): void {
    this.transport?.stop(when);
    const voices = this.voices; this.voices = [];
    for (const voice of voices) { try { voice.stop(when); } catch { /* already stopped */ } }
    const disconnect = () => voices.forEach(voice => { try { voice.disconnect(); } catch { /* closed context */ } });
    if (when > this.graph.currentTime) setTimeout(disconnect, Math.ceil((when - this.graph.currentTime) * 1000) + 10); else disconnect();
  }
  private assertLive(): void { if (this.disposed) throw new Error('Mixer has been disposed.'); }
  protected reportError(reason: string): void { this.pause(reason); this.status = 'error'; this.error = reason; this.emit(); }
  protected emit(): void { if (this.disposed) return; const state = this.snapshot(); for (const listener of this.listeners) listener(state); }
}
